/**
 * @file dcp/src/dcp-client/worker/supervisor2/options.js
 *
 * A support class for Supervisor2.
 * It is for managing Supervisor options and config access.
 *
 * @author Paul, paul@distributive.network
 * @date Feb-April 2023,
 * @module options
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
/* global dcpConfig */ // eslint-disable-line no-redeclare
// @ts-check
'use strict';

const DCP_ENV = require('dcp/common/dcp-env');
const { localStorage } = require('dcp/common/dcp-localstorage');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const { Address } = require('dcp/dcp-client/wallet/eth');
const constants = require('dcp/common/scheduler-constants');
const { ms } = require('dcp/utils');

const { Config } = require('./config');
const { selectiveSupEx, selectiveDebug2, debugBuild } = require('./common');

/** @typedef {string} opaqueId */ // 22 character base64 string
/** @typedef {import('./index').Supervisor} Supervisor                                    */
/** @typedef {import('dcp/dcp-client/wallet/eth').Address} Address                        */
/** @typedef {import('dcp/dcp-client/worker/index').DistributiveWorker} DistributiveWorker */
/** @typedef {import('dcp/utils/jsdoc-types').SandboxOptions} SandboxOptions              */
/** @typedef {import('dcp/utils/jsdoc-types').WorkerOptions} WorkerOptions                */
/** @typedef {import('dcp/utils/worker-and-daemons').SoteriaSharedHive} SoteriaSharedHive */

/**
 * It is important that options and config properties can be changed dynamically
 * and have the new values reflected in supervisor/worker operations.
 */
class SupervisorOptions extends Config
{
  /** Private class fields */
  /** @type {DistributiveWorker} */
  #worker;
  /** @type {{ cpu: number, gpu: number }} */
  #cores;
  /** @type {SoteriaSharedHive} */
  #soteriaHive;

  /**
   * @constructor
   * @param {DistributiveWorker} worker - Access worker.config and effectiveCores.
   */
  constructor(worker)
  {
    super();

    this.#worker = worker;
    /**
     * This is a reference to the configuration object supplied by the API consumer, and is
     * effectively part of the DistributiveWorker API. Changes to this object are intended to affect
     * the running supervisor eventually.
     * @type {WorkerOptions}
     */
    this.options = this.#worker.config;

    selectiveSupEx() && console.debug('Sup2:hardware, default core density, cores:', this.options.hardware, this.options.utilization,
                                      'cores.cpu', this.options.cores.cpu,
                                      'cores.gpu', this.options.cores.gpu,
                                      'maxSandboxes', this.options.maxSandboxes);

    const that = this;
    // Worker.effectiveCores is options.cores modified by options.utilization.
    // Alias effectiveCores as cores for ease of notation.
    this.#cores = {
      get cpu () { return that.#worker.effectiveCores.cpu; },
      get gpu () { return that.#worker.effectiveCores.gpu; }
    };
  }

  //
  // The SupervisorOptions accessors
  //

  /** @type {{ cpu: number, gpu: number }} */
  get cores () { return this.#cores; }
  /** @type {Address} */
  get paymentAddress () { return this.options.paymentAddress; }
  /** @type {Address} */
  set paymentAddress (addr) {
    if (!(addr instanceof Address))
      addr = new Address(this.options.paymentAddress);
    this.options.paymentAddress = addr;
  }
  /** @type {string[]|false} */
  get jobIds () { return this.options.jobIds; }
  /** @type {any[]} */
  get computeGroups () { return this.options.computeGroups; }
  /** @type {boolean} */
  get leavePublicGroup () { return this.options.leaveGlobalGroup; } /* deprecated jun 2025, remove once unit tests stop using this /wg */
  get leaveGlobalGroup () { return this.options.leaveGlobalGroup; }
  /** @type {*} */
  get schedulerConfig () { return this.options.schedulerConfig; }
  /** @type {boolean} */
  get allowConsoleAccess () { return this.options.allowConsoleAccess; }
  /** @type {SandboxOptions} */
  get sandboxOptions () { return this.options.sandboxOptions; }
  /** @type {boolean} */
  get trustScheduler () { return this.options.trustScheduler; }
  /** @type {{CPU: number, GPU: number, 'in': number, out: number}} */
  get minimumWage () { return this.options.minimumWage; }
  /** @type {{ any: string[], fetchData: string[], fetchWorkFunctions: string[], fetchArguments: string[], sendResults: string[] }} */
  get allowOrigins () { return this.options.allowOrigins; }
  /** @type {Array<{description: RegExp, device: RegExp}>} */
  get bannedGPUs () { return this.options.bannedGPUs; }
  /**
   * Enables the current scheduler to be an allowed origin.
   * Special symbol that's is not part of the type WorkerOptions.
   */
  get allowOriginsCurrentScheduler () { return this.options['__allowOriginsCurrentScheduler']; }

  /** @type {number} */
  get watchdogInterval ()
  {
    if (this.options.watchdogInterval)
      return ms(Number(this.options.watchdogInterval));
    return ms(Number(dcpConfig.supervisor?.tuning?.watchdogInterval || 7));
  }
  /** @type {number} */
  set watchdogInterval (wdInterval) { this.options.watchdogInterval = wdInterval; }

  /**
   * Each task returned by TD.requestTask is this.targetTaskDurationMs ms of work.
   * @type {number}
   */
  get targetTaskDurationMs () { return this.#soteriaHive?.targetTaskDurationMs || ms(180); }

  /**
   * Propagate to worker the value: dcpConfig.scheduler.services.soteria?.shared?.frequencyMs.
   * @type {number}
   */
  get soteriaFrequencyMs () { return this.#soteriaHive?.frequencyMs || ms(4 * 60); }

  /**
   * Propagate to worker the hive: dcpConfig.scheduler.services.soteria.shared,
   * as well as dcpConfig.scheduler.services.taskDistributor.tuning.targetTaskDuration,
   * all wrapped up in SoteriaSharedHive.
   * @type {SoteriaSharedHive}
   */
  set soteriaHive (sH)
  {
    this.#soteriaHive = sH;
    if (false || selectiveDebug2())
    {
      console.debug('Supervisor.options.soteriaHive:');
      console.debug('frequencyMs',         this.soteriaFrequencyMs);
      console.debug('debuggingFactor',     this.#soteriaHive.debuggingFactor);
      console.debug('useLifetimeAnalysis', this.useLifetimeAnalysis);
      console.debug('lifetimeFactor',      this.lifetimeFactor);

      console.debug('sliceMultiplier', this.sliceMultiplier);
      console.debug('sliceSigmas',     this.sliceSigmas);
      console.debug('sliceBiasMs',     this.sliceBiasMs);

      console.debug('beaconMs', this.#soteriaHive?.beaconMs);
      console.debug('targetTaskDuration', this.targetTaskDurationMs);
    }
  }

  /** @type {boolean} */
  get useLifetimeAnalysis () { return this.#soteriaHive?.useLifetimeAnalysis; }
  /** @type {number} */
  get lifetimeFactor ()  { return this.#soteriaHive?.lifetimeFactor || 1.0; }

  /** @type {number} */
  get sliceMultiplier () { return this.#soteriaHive?.sliceMultiplier || 1.5; }
  /** @type {number} */
  get sliceSigmas ()     { return this.#soteriaHive?.sliceSigmas || 2; }
  /** @type {number} */
  get sliceBiasMs ()     { return this.#soteriaHive?.sliceBiasMs || ms(2 * 60); }

   /** @type {number} */
  get maxSandboxes() { return this.options.maxSandboxes; }
  /**
   * Gets the logical and physical number of cores and also the total number of sandboxes the worker is allowed to run.
   * @returns {{ worker: opaqueId, lCores: number, pCores: number, sandbox: number}}
   */
  getStatisticsCPU ()
  {
    if (DCP_ENV.isBrowserPlatform)
    {
      return {
        worker: this.#worker.id,
        lCores: this.options.hardware?.vCores || window.navigator.hardwareConcurrency,
        pCores: window.navigator.hardwareConcurrency,
        sandbox: this.maxSandboxes,
      }
    }

    return {
      worker: this.#worker.id,
      lCores: this.options.hardware?.vCores || requireNative('os').cpus().length,
      pCores: getPhysicalCpuCount(),
      sandbox: this.maxSandboxes,
    }
  }
}
exports.SupervisorOptions = SupervisorOptions;

/**
 * The physical-cpu-count module forks a child to run lscpu on linux. If SIGINT is delivered exactly
 * when this process is run, it throws an exception, which propagates up the stack and looks (to the
 * user) like a bug in the worker.
 */
function getPhysicalCpuCount()
{
  try
  {
    return requireNative('physical-cpu-count');
  }
  catch(error)
  {
    if (error.signal)
      return 0;
    throw error;
  }
}
